// AGENDA — one calendar service (the one task engine, projected) for appointments, follow-ups, tasks,
// application actions, renewals, quote expiries, claims and moments. Day · Week · Month (spec §16).
import { useEffect, useMemo, useState } from 'react';
import { addDays, startOfWeek, isActivePolicy, nextPolicyAnniversary, nextAnnual, regionPack, type Task } from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath, useRoute } from '../core/router.ts';
import { printAgenda, printDayPlanner } from '../core/print.ts';
import { openers } from './forms.tsx';
import { AgendaRow } from './now.tsx';
import { DayPlanner } from './planner.tsx';
import { DailyGoalsCard } from './goals.tsx';

type Mode = 'day' | 'week' | 'month';
interface Marker { label: string; personId: string; kind: 'birthday' | 'renewal' | 'quote' | 'claim' }

export function AgendaView() {
  const rt = useRuntime();
  const { t, i18n, view, today, region } = rt;
  const [mode, setMode] = useState<Mode>('week');
  const [anchor, setAnchor] = useState(today);
  const fdow = regionPack(region).firstDayOfWeek;
  // the morning e-mail links here (#/agenda?sheet=YYYY-MM-DD): open that day and print its daily sheet once
  const route = useRoute();
  const sheet = route.query.get('sheet');
  useEffect(() => {
    if (!sheet || !/^\d{4}-\d{2}-\d{2}$/.test(sheet)) return;
    setMode('day'); setAnchor(sheet);
    navigate('/agenda', { replace: true });
    printDayPlanner(rt, sheet);
  }, [sheet]);
  // «Η μέρα μου» → «Σχέδιο ημέρας» links here (#/agenda?day=YYYY-MM-DD): that day's hour-by-hour plan
  const dayParam = route.query.get('day');
  useEffect(() => {
    if (!dayParam || !/^\d{4}-\d{2}-\d{2}$/.test(dayParam)) return;
    setMode('day'); setAnchor(dayParam);
    navigate('/agenda', { replace: true });
  }, [dayParam]);
  const range = useMemo(() => {
    if (mode === 'day') return [anchor];
    if (mode === 'week') { const f = startOfWeek(anchor, fdow); return Array.from({ length: 7 }, (_, i) => addDays(f, i)); }
    const f = startOfWeek(`${anchor.slice(0, 7)}-01`, fdow);
    return Array.from({ length: 42 }, (_, i) => addDays(f, i));
  }, [mode, anchor, fdow]);
  const from = range[0]!, to = range[range.length - 1]!;
  const tasks = useMemo(() => view.rows('tasks').filter((x) => x.status !== 'cancelled'), [view]);
  const byDay = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const x of tasks) if (x.dueDate && x.dueDate >= from && x.dueDate <= to) m.set(x.dueDate, [...(m.get(x.dueDate) ?? []), x]);
    for (const l of m.values()) l.sort((a, b) => (a.dueTime ?? '99').localeCompare(b.dueTime ?? '99'));
    return m;
  }, [tasks, from, to]);
  const overdue = tasks.filter((x) => x.status === 'open' && x.dueDate && x.dueDate < today).sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''));
  const unscheduled = tasks.filter((x) => x.status === 'open' && !x.dueDate);
  // markers: birthdays, policy anniversaries, quote expiries, claim actions (v121 program calendar)
  const markers = useMemo(() => {
    const m = new Map<string, Marker[]>();
    const add = (d: string, mk: Marker) => { if (d >= from && d <= to) m.set(d, [...(m.get(d) ?? []), mk]); };
    for (const p of view.rows('persons')) if (!p.archivedAt && p.birthDate && p.stage !== 'dependent') add(nextAnnual(p.birthDate, from), { label: `🎂 ${p.displayName}`, personId: p.id, kind: 'birthday' });
    for (const pol of view.rows('policies')) {
      if (!isActivePolicy(pol)) continue;
      const d = nextPolicyAnniversary(pol, from);
      const p = view.get('persons', pol.personId);
      if (d && p && !p.archivedAt) add(d, { label: `↻ ${pol.productName} · ${p.displayName}`, personId: p.id, kind: 'renewal' });
    }
    for (const q of view.rows('quotations')) if (q.expiryDate && (q.status === 'sent' || q.status === 'draft')) add(q.expiryDate, { label: `⌛ ${q.product} · ${view.get('persons', q.personId)?.displayName ?? ''}`, personId: q.personId, kind: 'quote' });
    for (const c of view.rows('claims')) if (c.nextActionDate && !['paid', 'declined', 'closed'].includes(c.status)) add(c.nextActionDate, { label: `✚ ${c.type} · ${view.get('persons', c.personId)?.displayName ?? ''}`, personId: c.personId, kind: 'claim' });
    return m;
  }, [view, from, to]);
  const step = (dir: 1 | -1) => setAnchor(mode === 'day' ? addDays(anchor, dir) : mode === 'week' ? addDays(anchor, dir * 7) : `${new Date(Date.UTC(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)) - 1 + dir, 1)).toISOString().slice(0, 10)}`);
  const title = mode === 'day' ? i18n.date(anchor, 'long') : mode === 'week' ? `${i18n.date(range[0]!, 'dayMonth')} – ${i18n.date(range[6]!, 'medium')}` : new Intl.DateTimeFormat(rt.locale, { month: 'long', year: 'numeric' }).format(new Date(`${anchor.slice(0, 7)}-15T12:00:00Z`));
  const printDays = (mode === 'month' ? range.filter((d) => d.slice(0, 7) === anchor.slice(0, 7)) : range).map((d) => ({ day: d, tasks: byDay.get(d) ?? [] }));

  const Slot = ({ x, compact }: { x: Task; compact?: boolean }) => {
    const p = x.personId ? view.get('persons', x.personId) : undefined;
    const pr = x.prospectId ? view.get('prospects', x.prospectId) : undefined;
    return (
      <div className={`slot ${x.kind === 'appointment' ? 'appointment' : ''} ${x.status === 'done' ? 'done' : ''} ${x.status === 'open' && x.dueDate && x.dueDate < today ? 'overdue' : ''}`} onClick={() => (x.status === 'open' ? openers(rt).complete(x) : p && navigate(personPath(p.id)))} role="button" tabIndex={0} data-testid="slot">
        <b className={compact ? 'one-line' : ''}>{x.dueTime ? i18n.time(x.dueTime) + ' · ' : ''}{i18n.maybe(x.title)}</b>
        {!compact && <span className="muted">{p?.displayName ?? pr?.name ?? i18n.enumLabel('taskKind', x.kind)}</span>}
      </div>
    );
  };
  const Markers = ({ d }: { d: string }) => <>{(markers.get(d) ?? []).map((mk, i) => <div key={i} className={`slot marker ${mk.kind}`} onClick={() => navigate(personPath(mk.personId))}>{mk.label}</div>)}</>;

  return (
    <div data-testid="agenda">
      <div className="page-head">
        <div><h1>{t('agenda.title')}</h1><p>{title}</p></div>
        <div className="page-actions">
          <Seg values={['day', 'week', 'month'] as const} value={mode} onChange={setMode} label={(x) => t(`agenda.mode.${x}`)} testId="agenda-mode" />
          <button className="btn btn-icon" aria-label={t('agenda.prev')} onClick={() => step(-1)}><Icon name="back" /></button>
          <button className="btn" onClick={() => setAnchor(today)}>{t('common.today')}</button>
          <button className="btn btn-icon" aria-label={t('agenda.next')} onClick={() => step(1)}><Icon name="arrow" /></button>
          <button className="btn" onClick={() => printAgenda(rt, printDays)}><Icon name="print" />{t('agenda.print')}</button>
          <button className="btn" onClick={() => printDayPlanner(rt, mode === 'day' ? anchor : today)} data-testid="agenda-day-sheet" title={t('agenda.day_sheet_hint')}><Icon name="document" />{t('agenda.day_sheet')}</button>
          <button className="btn" onClick={() => openers(rt).followup(null)}><Icon name="followup" />{t('task.new_followup')}</button>
          <button className="btn btn-primary" onClick={() => openers(rt).appointment(null)}><Icon name="plus" />{t('task.new_appointment')}</button>
        </div>
      </div>

      {mode === 'week' && <div className="week" data-testid="week">
        {range.map((d) => (
          <section key={d} className={`day${d === today ? ' today' : ''}`}>
            <div className="day-head"><b role="link" style={{ cursor: 'pointer' }} onClick={() => { setAnchor(d); setMode('day'); }}>{i18n.date(d, 'weekday')}</b>{d === today && <span className="chip gold">{t('common.today')}</span>}</div>
            <Markers d={d} />
            {(byDay.get(d) ?? []).map((x) => <Slot key={x.id} x={x} />)}
            {!(byDay.get(d)?.length) && !(markers.get(d)?.length) && <span className="tiny muted" style={{ padding: 4 }}>{t('agenda.no_items')}</span>}
          </section>
        ))}
      </div>}

      {mode === 'month' && <div className="month-grid" data-testid="month">
        {range.slice(0, 7).map((d) => <div key={d} className="month-dow">{new Intl.DateTimeFormat(rt.locale, { weekday: 'short' }).format(new Date(`${d}T12:00:00Z`))}</div>)}
        {range.map((d) => {
          const items = byDay.get(d) ?? [];
          const mk = markers.get(d) ?? [];
          const out = d.slice(0, 7) !== anchor.slice(0, 7);
          return (
            <section key={d} className={`mday${d === today ? ' today' : ''}${out ? ' out' : ''}`} onClick={() => { setAnchor(d); setMode('day'); }} role="button" tabIndex={0}>
              <span className="mnum">{Number(d.slice(8))}</span>
              {items.slice(0, 3).map((x) => <span key={x.id} className={`mdot ${x.kind === 'appointment' ? 'appt' : ''} ${x.status !== 'open' ? 'done' : ''}`}>{x.dueTime ? i18n.time(x.dueTime) + ' ' : ''}{i18n.maybe(x.title)}</span>)}
              {items.length > 3 && <span className="tiny muted">+{items.length - 3}</span>}
              {mk.length > 0 && <span className="tiny muted">{mk.map((x) => x.label.slice(0, 2)).join(' ')}</span>}
            </section>
          );
        })}
      </div>}

      {mode === 'day' && <div className="agenda-day-cols" data-testid="day">
        {/* handoff v08 #23: the day is one editable hour-by-hour plan (modules/planner.tsx); untimed items stay listed */}
        <div className="stack" style={{ gap: 12 }}>
          {((markers.get(anchor) ?? []).length > 0 || (byDay.get(anchor) ?? []).some((x) => !x.dueTime)) && <section className="card card-pad">
            <div className="stack" style={{ gap: 6 }}>
              <Markers d={anchor} />
              {(byDay.get(anchor) ?? []).filter((x) => !x.dueTime).map((x) => <Slot key={x.id} x={x} />)}
            </div>
          </section>}
          <DayPlanner day={anchor} onDay={setAnchor} />
        </div>
        <div className="stack" style={{ gap: 12 }}>
        <DailyGoalsCard day={anchor} compact />
        <section className="card card-pad">
          <div className="section-title">{t('agenda.day_summary')}</div>
          <div className="kv">
            <div><span>{t('enum.taskKind.appointment')}</span><b>{(byDay.get(anchor) ?? []).filter((x) => x.kind === 'appointment').length}</b></div>
            <div><span>{t('enum.taskKind.call')}</span><b>{(byDay.get(anchor) ?? []).filter((x) => x.kind === 'call').length}</b></div>
            <div><span>{t('enum.taskKind.followup')}</span><b>{(byDay.get(anchor) ?? []).filter((x) => x.kind === 'followup').length}</b></div>
            <div><span>{t('agenda.markers')}</span><b>{(markers.get(anchor) ?? []).length}</b></div>
          </div>
        </section>
        </div>
      </div>}

      <div className="grid grid-2" style={{ marginTop: 18 }}>
        <section className="card"><div className="card-head"><h3>{t('agenda.overdue')} · {overdue.length}</h3></div>
          {overdue.length ? <div className="agenda-list">{overdue.map((x) => <AgendaRow key={x.id} task={x} />)}</div> : <Empty icon="check">{t('common.empty')}</Empty>}
        </section>
        <section className="card"><div className="card-head"><h3>{t('agenda.unscheduled')} · {unscheduled.length}</h3></div>
          {unscheduled.length ? <div className="agenda-list">{unscheduled.map((x) => <AgendaRow key={x.id} task={x} />)}</div> : <Empty icon="task">{t('common.empty')}</Empty>}
        </section>
      </div>
    </div>
  );
}
